import ServiceError from '../../models/classes/ServiceError';
import {
  CartResponseWarning,
  CartUpdateContract,
  ICartService,
} from '../../types/Cart';
import { CartServiceConfig } from '../../types/ServiceConfigurations';

const InvalidCartIdError = new ServiceError('CartService', 'invalid CartId');
const ApiError = new ServiceError('CartService', 'error contacting API');
const NoDataError = new ServiceError('CartService', 'no cart data returned');

function doesResponseContainShoppingCartRedirect(
  responseHtml: string
): boolean {
  return (
    responseHtml.indexOf("window.location.replace('/ShoppingCart.asp?')") > -1
  );
}

/// Function to fix some of the inconsistencies in the data coming back
/// from the API.
function cleanCartData(cartResponse: CartUpdateContract): CartUpdateContract {
  return {
    ...cartResponse,
    giftCards: (cartResponse.giftCards ?? []).filter(
      giftCard => !!giftCard.code
    ),
    totals: {
      ...cartResponse.totals,
      giftCards: cartResponse.totals.giftCards ?? 0,
    },
  };
}

class CartService implements ICartService {
  private storeUrl: string;

  constructor(config: CartServiceConfig) {
    this.storeUrl = config.apiUrl;
  }

  public async getCartDataAsync(
    cartId: string | null
  ): Promise<CartUpdateContract> {
    if (!cartId) {
      throw InvalidCartIdError;
    }
    try {
      const response = await window.fetch(
        `${this.storeUrl}/api/v1/carts/${cartId ?? ''}`,
        { credentials: 'include' }
      );
      const responseJsonEnvelope = await response.json();
      if (responseJsonEnvelope?.data) {
        return cleanCartData(responseJsonEnvelope.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NoDataError;
  }

  public async postCartDataAsync(
    cartId: string | null,
    cart: CartUpdateContract
  ): Promise<CartUpdateContract> {
    if (!cartId) {
      throw InvalidCartIdError;
    }
    try {
      const response = await window.fetch(
        `${this.storeUrl}/api/v1/carts/${cartId ?? ''}`,
        {
          credentials: 'include',
          method: 'POST',
          headers: {
            vMethod: 'PUT',
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cart),
        }
      );

      const responseJsonEnvelope = await response.json();
      if (responseJsonEnvelope?.data) {
        return {
          ...cleanCartData(responseJsonEnvelope.data),
          warnings: responseJsonEnvelope.warnings,
        };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NoDataError;
  }

  private async getShoppingCartPage(): Promise<string> {
    try {
      const response = await window.fetch(`${this.storeUrl}/ShoppingCart.asp`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          Accept: 'text/html',
        },
      });
      return await response.text();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
  }

  public async updateQuantity(
    cart: CartUpdateContract
  ): Promise<CartResponseWarning[]> {
    try {
      const formData = new URLSearchParams();
      // loop through the items and the kits within the items
      // and create an array of quantities for each item and kit
      const flattenedCartItemQuantities = cart.items.reduce((acc, item) => {
        const { qty, kits } = item;
        acc.push(qty);

        if (kits?.length > 0) {
          acc.push(...kits.map(k => k.quantity));
        }
        return acc;
      }, [] as number[]);

      flattenedCartItemQuantities.forEach((itemQuantity, index) => {
        const fieldName = `Quantity${index + 1}`;
        formData.append(fieldName, itemQuantity.toString());
      });
      formData.append('btnCouponCode', 'Apply');

      let response = await this.postUpdateCartToShoppingCartPage(formData);

      if (doesResponseContainShoppingCartRedirect(response)) {
        response = await this.getCartWarningsFromShoppingCart(3);
      }
      return this.getWarningsFromHtml(response);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
  }

  private async postUpdateCartToShoppingCartPage(
    formData: URLSearchParams
  ): Promise<string> {
    try {
      const htmlResponse = await window.fetch(
        `${this.storeUrl}/ShoppingCart.asp`,
        {
          credentials: 'include',
          method: 'POST',
          headers: {
            Accept: 'text/html',
            'Content-Type': 'application/x-www-form-urlencoded',
            'V-Platform': 'classic',
          },
          body: formData,
        }
      );
      return await htmlResponse.text();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private getWarningsFromHtml(
    response: string,
    discountCode?: string
  ): CartResponseWarning[] {
    const warnings: CartResponseWarning[] = [];

    if (discountCode) {
      if (
        response.indexOf(
          'Please double-check that the characters you entered are normal A-Z or 0-9'
        ) > 0
      ) {
        warnings.push({
          Code: 'couponCode',
          Message: `Coupon code '${discountCode} is not valid.`,
        });
        return warnings;
      }
    }

    const doc = new DOMParser().parseFromString(response, 'text/html');

    const errorListItems = doc.querySelectorAll(
      '.v65-error-list-text > ul > li'
    );
    if ((errorListItems ?? []).length > 0) {
      errorListItems.forEach(errorListItem => {
        const errorMessage = errorListItem.textContent;
        warnings.push({
          Code: 'couponCode',
          Message: errorMessage ?? '',
        });
      });
    }
    return warnings;
  }

  private async getCartWarningsFromShoppingCart(
    retryCount: number
  ): Promise<string> {
    if (retryCount === 0) {
      return '';
    }
    const response = await this.getShoppingCartPage();
    if (!doesResponseContainShoppingCartRedirect(response)) {
      return response;
    }
    return this.getCartWarningsFromShoppingCart(retryCount - 1);
  }

  public async applyDiscount(
    discountCode: string,
    cart: CartUpdateContract
  ): Promise<CartResponseWarning[]> {
    try {
      const formData = new URLSearchParams();
      formData.append('CouponCode', discountCode);
      // loop through the items and the kits within the items
      // and create an array of quantities for each item and kit
      const flattenedCartItemQuantities = cart.items.reduce((acc, item) => {
        const { qty, kits } = item;
        acc.push(qty);
        if (item.kits?.length > 0) {
          acc.push(...kits.map(k => k.quantity));
        }
        return acc;
      }, [] as number[]);

      flattenedCartItemQuantities.forEach((itemQuantity, index) => {
        const fieldName = `Quantity${index + 1}`;
        formData.append(fieldName, itemQuantity.toString());
      });
      formData.append('btnCouponCode', 'Apply');

      let response = await this.postUpdateCartToShoppingCartPage(formData);

      if (doesResponseContainShoppingCartRedirect(response)) {
        response = await this.getCartWarningsFromShoppingCart(3);
      }
      return this.getWarningsFromHtml(response, discountCode);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
  }

  public async removeDiscount(
    discountCode: string,
    cartId: string,
    isSiteDesignerEnabled: boolean
  ): Promise<void> {
    try {
      let response;

      // Non element stores will use the logic in ShoppingCart.asp found in VolusionPlatform
      if (!isSiteDesignerEnabled) {
        response = await window.fetch(
          `${this.storeUrl}/ShoppingCart.asp?RemoveCouponCode=${discountCode}`
        );
      } else {
        // Element stores don't have access to that logic, so they will hit this endpoint instead
        response = await window.fetch(
          `${this.storeUrl}/api/v2/storefront/cartCoupons/${discountCode
            .toLowerCase()
            .trim()}`,
          { headers: { Cookie: `CartID5=${cartId}` }, method: 'DELETE' }
        );
      }

      // eslint-disable-next-line no-useless-return
      if (response.status === 200) return;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
  }
}

export default CartService;
